<script setup>


import DropDown from '@/Components/Form/DropDown.vue'
import {applyCorporationType, corporationTypeErrors} from '@/support/einEntityType'
import {VueSignaturePad} from 'vue-signature-pad';
import {defineEmits, onMounted, watch, reactive, toRaw, ref, computed} from "vue";
import axios from 'axios';
import {TrashIcon, ArrowDownTrayIcon, ArrowRightIcon} from "@heroicons/vue/20/solid";
import '@vuepic/vue-datepicker/dist/main.css';
import moment from "moment/moment";


const emit = defineEmits(['remove', 'reloadInfo', 'updateFormData'])

const props = defineProps({
  formData: {
    type: Object,
    required: false,
    default: null,
  },
  hasPrimaryEin: {
    type: Boolean,
    required: false,
    default: false,
  },
  itemUuid: {
    type: String,
    required: false,
    default: null,
  },
  brandUuid: {
    type: String,
    required: false,
    default: null,
  },

})

const validationErrors = ref('US');
const country = ref('US');


const corporationValues = [
  {id: 1, name: 'C-Corp'},
  {id: 2, name: 'S-Corp'}
];
// const signaturePadFss4 = ref(null);
// const signaturePadF8821 = ref(null);


// const form = reactive({
//   ein_first_name: null,
//   ein_last_name: null,
//   ein_county: null,
//   ein_ssn: null,
//   ein_member_number: null,
//   ein_merchandise: null,
//   ein_date_start: null,
//   ein_entity_type: null,
//   ein_before: 0,
//   ein_before_specified: null,
//   ein_reason: null,
//   ein_activity: null,
//   ein_further_info: null,
//   ein_entity_type_specified: null,
//   ein_entity_type_corporation_specified: null,
//   question: {heavy_vehicle: null, gambling: null, w2: null, guns: null, quarterly: null},
//   signature_fss4: null,
//   signature_f8821: null,
//
//
//   errors: {
//     ein_first_name: null,
//     ein_last_name: null,
//     ein_county: null,
//     ein_ssn: null,
//     ein_member_number: null,
//     ein_merchandise: null,
//     ein_date_start: null,
//     ein_entity_type: null,
//     ein_before: null,
//     ein_before_specified: null,
//     ein_reason: null,
//     ein_activity: null,
//     ein_activity_specified: null,
//     ein_further_info: null,
//     ein_entity_type_specified: null,
//     ein_entity_type_corporation_specified: null,
//   }
// });

const form = props.formData;

const generating = ref(false);
const ss4Url = ref(null);
const f8821Url = ref(null);
const generateError = ref(null);
const submitGateError = ref(null);
const signaturePad = ref(null);
const signatureEmpty = ref(true);

const formsReady = computed(() => !!ss4Url.value && !!f8821Url.value);

// The "Click here →" + red-border nudge is deferred: it stays hidden on page
// load and only flips on after the parent's submit handler calls
// validateForSubmit() and discovers the customer hasn't completed the
// signature + generate-forms step yet. It clears the moment they finish.
const generateNudgeArmed = ref(false);
const needsGenerateNudge = computed(
  () => !props.itemUuid && generateNudgeArmed.value && !formsReady.value
);

const mediaUrl = (m) => m?.original_url || (m?.uuid ? `/media/${m.uuid}` : null);

const onSignatureBegin = () => {
  signatureEmpty.value = false;
  submitGateError.value = null;
};

const clearSignature = () => {
  signaturePad.value?.clearSignature();
  signatureEmpty.value = true;
  if (form?.ein) {
    form.ein.signature = null;
  }
};

const captureSignature = () => {
  if (!signaturePad.value) return null;
  const { isEmpty, data } = signaturePad.value.saveSignature();
  if (isEmpty) return null;
  return data;
};

// Backend validation keys are namespaced under `meta_data.` because the
// preview endpoint receives the form under that key. The template error
// bindings, however, read `form.errors['ein.<field>']` etc. — same as the
// order submit path. Strip the prefix so the inline per-field messages
// actually find their matching key.
const adaptPreviewErrors = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const adapted = {};
  for (const [key, value] of Object.entries(raw)) {
    const stripped = key.startsWith('meta_data.') ? key.slice('meta_data.'.length) : key;
    // Defensive: the order-submit gate keys (tos, forms_reviewed_confirmed,
    // forms_temp_token) have no place in the Generate response. Drop them so
    // they can't be surfaced as Generate-time errors even if the backend
    // ever changes shape.
    if (orderSubmitOnlyErrorKeys.includes(stripped)) continue;
    adapted[stripped] = value;
  }
  return adapted;
};

// Keys that the preview endpoint validates. Mirrors the rules in
// EINFormPreviewRequest so a re-click of Generate clears stale red borders
// across both the EIN section and the personal-info section above it.
const previewValidatedKeys = new Set([
  'brand_uuid', 'signature',
  'first_name', 'last_name', 'email', 'phone',
  'address', 'city', 'state', 'zip', 'company_name',
]);

// Keys that belong to the order-submit gate, not the preview gate. We
// explicitly null them out at the start of Generate so a stale error from a
// prior Submit Order attempt (e.g. unchecked T&C) doesn't get re-displayed as
// if Generate cared about it.
const orderSubmitOnlyErrorKeys = ['tos', 'ein.forms_reviewed_confirmed', 'ein.forms_temp_token'];

const clearPreviewFieldErrors = () => {
  if (!form?.errors) return;
  for (const key of Object.keys(form.errors)) {
    if (
      key.startsWith('ein.') ||
      previewValidatedKeys.has(key) ||
      orderSubmitOnlyErrorKeys.includes(key)
    ) {
      form.errors[key] = null;
    }
  }
};

const generateReviewForms = async () => {
  if (generating.value) return;
  generateError.value = null;
  submitGateError.value = null;
  clearPreviewFieldErrors();
  generating.value = true;
  try {
    if (props.itemUuid) {
      // Item exists (admin re-review): use the Item-bound endpoints.
      const fd = new FormData();
      fd.append('uuid', props.itemUuid);
      const [ss4Resp, f8821Resp] = await Promise.all([
        axios.post(route('api.items.get.ss4.to.sign'), fd),
        axios.post(route('api.items.get.ein.authorization.to.sign'), fd),
      ]);
      ss4Url.value = mediaUrl(ss4Resp.data?.media);
      f8821Url.value = mediaUrl(f8821Resp.data?.media);
      emit('reloadInfo', true);
    } else {
      // Pre-order checkout: render previews from form data into a signed temp location.
      if (!props.brandUuid) {
        generateError.value = 'Brand context is missing; cannot generate preview.';
        return;
      }
      const signature = captureSignature();
      if (!signature) {
        generateError.value = 'Please sign in the signature pad before generating the forms.';
        return;
      }
      if (form?.ein) {
        form.ein.signature = signature;
      }
      const resp = await axios.post(route('public.api.ein.preview.generate'), {
        brand_uuid: props.brandUuid,
        meta_data: form,
        signature,
      });
      // ApiResponseTrait wraps payload under `data`, so the URLs live at resp.data.data.*
      const payload = resp.data?.data || resp.data || {};
      ss4Url.value = payload.ss4_url || null;
      f8821Url.value = payload.f8821_url || null;
      if (payload.token) {
        form.ein.forms_temp_token = payload.token;
      }
    }
  } catch (e) {
    console.error('Failed to generate EIN review forms:', e);
    const rawErrors = e?.response?.data?.errors;
    if (rawErrors && typeof rawErrors === 'object') {
      // Mirror the order-submit pattern: hydrate per-field errors so the
      // inline red borders + "this field is required"-style messages render
      // next to the offending inputs.
      const adapted = adaptPreviewErrors(rawErrors);
      if (form && form.errors) {
        Object.assign(form.errors, adapted);
      }
      generateError.value = 'Please correct the highlighted fields and try again.';
    } else {
      generateError.value = e?.response?.data?.message
          || 'Failed to generate forms. Please complete all required fields and try again.';
    }
  } finally {
    generating.value = false;
  }
};


const submitForm = () => {
  emit('updateFormData', form);
}

// Live-format the SSN/ITIN/EIN field as XXX-XX-XXXX. We strip every non-digit
// (so a paste of "123-45-6789" or "123 45 6789" still works), cap at 9 digits,
// then re-inject the dashes at the canonical positions.
const onSsnInput = (event) => {
  const digits = (event.target.value || '').replace(/\D/g, '').slice(0, 9);
  let formatted = digits;
  if (digits.length > 5) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } else if (digits.length > 3) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (form?.ein) {
    form.ein.ein_ssn = formatted;
  }
  // Belt-and-suspenders: if Vue would skip the re-render because the model
  // value didn't change (e.g. user typed a trailing non-digit that got
  // stripped), push the formatted value into the DOM directly.
  if (event.target.value !== formatted) {
    event.target.value = formatted;
  }
};

// "Generate forms for review" (and the signature it depends on) is optional —
// the customer may place an EIN order without it. So this no longer gates the
// submit or arms the visual nudge; it just clears any lingering gate state.
// Kept and still exposed so OrderSection can call it unconditionally.
const validateForSubmit = () => {
  submitGateError.value = null;
  generateNudgeArmed.value = false;
  return true;
};

// No-op now that the backend no longer rejects orders missing the EIN preview
// token. Retained for the exposed contract; generating the forms is optional,
// so we never re-arm the nudge.
const armGenerateNudge = () => {};

defineExpose({ validateForSubmit, armGenerateNudge });

const remove = () => {
  emit('remove', true);
}

// The order payload nests every EIN answer under `form.ein`, and the backend
// validates the nested key (App\Rules\ForceCorporationTypeOnCorporationEntity
// reads request('ein.ein_entity_type_corporation_specified')). Assigning to the
// top level of the form instead left the dropdown showing "C-Corp" while the
// submitted value was null, so every EIN order on a Corporation-classified
// document came back 422 with "You must specify the type of tax form you will
// file." and nothing highlighted.
const handleEntityTypeCorporationSpecified = (value) => {
  applyCorporationType(form, value);
}


watch(form, (newForm) => {
  emit('updateFormData', newForm);

}, {deep: true});



onMounted(() => {
  if (form?.ein) {
    if (form.ein.forms_reviewed_confirmed === undefined) {
      form.ein.forms_reviewed_confirmed = false;
    }
    if (form.ein.forms_temp_token === undefined) {
      form.ein.forms_temp_token = null;
    }
    if (form.ein.signature === undefined) {
      form.ein.signature = null;
    }
    if (form.ein.fss4_to_sign?.length) ss4Url.value = mediaUrl(form.ein.fss4_to_sign[0]);
    if (form.ein.f8821_to_sign?.length) f8821Url.value = mediaUrl(form.ein.f8821_to_sign[0]);
  }



});

</script>

<template>


  <div class="mt-10 border border-gray-200 pt-10 p-3">
    <div class="bg-emerald-500 text-white">
      <div v-if="!hasPrimaryEin" class="float-right flex ">
        <button type="button"
                class="-m-0.0 flex p-2.5  hover:text-indigo-300 bg-emerald-500 text-white">

          <span class="sr-only">Remove</span>
          <TrashIcon @click="remove()" class="h-7 w-7" aria-hidden="true"/>
        </button>
      </div>
      <h2 class="text-xl font-bold uppercase text-center">EIN Registration Information</h2>
      <h3 class="text-md font-medium text-center">(Required if ordering the EIN Registration Option)</h3>
    </div>
    <h2 class="text-md font-medium mt-2">1. Responsible Party</h2>
    <div class="mt-2 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">

      <div>
        <label for="ein-first-name" class="block text-sm font-medium text-gray-700">First name</label>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_first_name']}" v-model="form.ein.ein_first_name"
                 type="text" id="ein-first-name" name="first-name" autocomplete="given-name"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
          <div v-if="form.errors['ein.ein_first_name']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_first_name']"
                  :key="index">{{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <label for="ein-last-name" class="block text-sm font-medium text-gray-700">Last name</label>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_last_name']}" v-model="form.ein.ein_last_name"
                 type="text" id="ein-last-name" name="last-name" autocomplete="family-name"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
          <div v-if="form.errors['ein.ein_last_name']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_last_name']" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <label for="ein-county" class="block text-sm font-medium text-gray-700">County in {{ form.jurisdictionName }}</label>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_county']}" v-model="form.ein.ein_county"
                 type="text" name="county" id="ein-county"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
          <div v-if="form.errors['ein.ein_county']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_county']" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <label for="ein-ssn" class="block text-sm font-medium text-gray-700">
          SSN, ITIN, or EIN of Responsible Party</label>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_ssn']}"
                 :value="form.ein.ein_ssn"
                 @input="onSsnInput"
                 type="text"
                 name="ssn" id="ein-ssn"
                 inputmode="numeric"
                 maxlength="11"
                 autocomplete="off"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder="XXX-XX-XXXX"/>
          <div v-if="form.errors['ein.ein_ssn']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_ssn']" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">2. Type of Entity (Select One)</h2>
      </div>
      <div class="sm:col-span-2">
        <div class="space-y-5">
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="LLC" id="ein-entity-type-llc"
                     aria-describedby="medium-description" name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-llc" class="font-medium text-gray-900">Limited Liability Company</label>
            </div>
          </div>
          <div class="relative flex items-start">

            <div class="flex h-6 items-center">

              <input v-model="form.ein.ein_entity_type" value="corporation" id="ein-entity-type-corporation" aria-describedby="small-description"
                     name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6 w-2/4">
              <label for="ein-entity-type-corporation" class="font-medium text-gray-900">Corporation (specify)</label>
            </div>

            <div v-if="form.ein.ein_entity_type==='corporation'" class="w-full">
              <DropDown
                  class="w-full"
                  @update:modelValue="handleEntityTypeCorporationSpecified"
                  :values="corporationValues"
              ></DropDown>
              <ul v-if="corporationTypeErrors(form).length" class="mt-1">
                <li class="text-red-600 text-sm" v-for="(error, index) in corporationTypeErrors(form)" :key="index">
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>


          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="church" id="ein-entity-type-church"
                     aria-describedby="medium-description" name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-church" class="font-medium text-gray-900">Church or Church Controller Organization</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="other_non_profit" id="ein-entity-type-other-non-profit"
                     aria-describedby="medium-description" name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-other-non-profit" class="font-medium text-gray-900">Other Non-Profit</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="sole_proprietorship" id="ein-entity-type-sole-proprietorship"
                     aria-describedby="small-description" name="ein_entity_type" type="radio" checked
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-sole-proprietorship" class="font-medium text-gray-900">Sole Proprietor</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="trust" id="ein-entity-type-trust"
                     aria-describedby="medium-description" name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-trust" class="font-medium text-gray-900">Trust</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_entity_type" value="partnership" id="ein-entity-type-partnership"
                     aria-describedby="medium-description" name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-entity-type-partnership" class="font-medium text-gray-900">Partnership</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">

              <input v-model="form.ein.ein_entity_type" value="other" id="ein-entity-type-other" aria-describedby="small-description"
                     name="ein_entity_type" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6 w-2/4">
              <label for="ein-entity-type-other" class="font-medium text-gray-900">Other (specify)</label>
            </div>
            <input
                v-if="form.ein.ein_entity_type==='other'"
                :class="{'border-2 border-red-500': form.errors['ein.ein_entity_type_specified']}"
                v-model="form.ein.ein_entity_type_specified" type="text"
                name="ein_entity_type_specified" id="ein-entity-type-specified" autocomplete="off"
                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter Entity Type"/>

          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">3. Number of Owners or Members?</h2>

      </div>
      <div class="sm:col-span-2">
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_member_number']}"
                 v-model="form.ein.ein_member_number" type="text"
                 name="ein_member_number" id="ein-member-number" autocomplete="off"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder="1"/>
          <div v-if="form.errors['ein.ein_member_number']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_member_number']" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">4. Reason for Applying (Select One)</h2>

      </div>
      <div class="sm:col-span-2">
        <div class="space-y-5">
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_reason" value="Started new business" id="ein-reason-started-new-business"
                     aria-describedby="small-description" name="reason" type="radio" checked
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-reason-started-new-business" class="font-medium text-gray-900">Started new business</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_reason" value="Hired employees" id="ein-reason-hired-employees"
                     aria-describedby="medium-description" name="reason" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-reason-hired-employees" class="font-medium text-gray-900">Hired employees</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_reason" value="Banking purpose" id="ein-reason-banking-purpose"
                     aria-describedby="medium-description" name="reason" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-reason-banking-purpose" class="font-medium text-gray-900">Banking purpose</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_reason" value="Purchased ongoing business" id="ein-reason-purchased-ongoing-business"
                     aria-describedby="medium-description" name="reason" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-reason-purchased-ongoing-business" class="font-medium text-gray-900">Purchased ongoing business</label>
            </div>
          </div>


        </div>
      </div>
      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">5. Date Business Started</h2>
      </div>
      <div>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_date_start']}" v-model="form.ein.ein_date_start"
                 type="text"
                 name="ein_date_start" id="ein-date-start" autocomplete="off"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder="MM/DD/YEAR"/>
          <div v-if="form.errors['ein.ein_date_start']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_date_start']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">6. Check one box that best describes the principal activity of your
          business.</h2>
      </div>


      <div class="sm:col-span-2">

        <div class="space-y-5">
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Construction" id="ein-activity-construction"
                     aria-describedby="small-description" name="act" type="radio" checked
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-construction" class="font-medium text-gray-900">Construction</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Rental & leasing" id="ein-activity-rental-leasing"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-rental-leasing" class="font-medium text-gray-900">Rental & leasing</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Transportation & warehousing" id="ein-activity-transportation-warehousing"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-transportation-warehousing" class="font-medium text-gray-900">Transportation & warehousing</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Real estate" id="ein-activity-real-estate"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-real-estate" class="font-medium text-gray-900">Real estate</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Finance & insurance" id="ein-activity-finance-insurance"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-finance-insurance" class="font-medium text-gray-900">Finance & insurance</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Health care & social assistance" id="ein-activity-health-care-social-assistance"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-health-care-social-assistance" class="font-medium text-gray-900">Health care & social assistance</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Manufacturing" id="ein-activity-manufacturing"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-manufacturing" class="font-medium text-gray-900">Manufacturing</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Wholesale-agent/broker" id="ein-activity-wholesale-agent-broker"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-wholesale-agent-broker" class="font-medium text-gray-900">Wholesale-agent/broker</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Accommodation & food service" id="ein-activity-accommodation-food-service"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-accommodation-food-service" class="font-medium text-gray-900">Accommodation & food service</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Wholesale-other" id="ein-activity-wholesale-other"
                     aria-describedby="medium-description" name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-wholesale-other" class="font-medium text-gray-900">Wholesale-other</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_activity" value="Retail" id="ein-activity-retail" aria-describedby="medium-description"
                     name="act" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-activity-retail" class="font-medium text-gray-900">Retail</label>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">

              <input v-model="form.ein.ein_activity" value="Other" id="ein-activity-other" aria-describedby="small-description"
                     name="act" type="radio" checked
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">

            </div>
            <div class="ml-3 text-sm leading-6 w-2/4">
              <label for="ein-activity-other" class="font-medium text-gray-900">Other (specify)</label>
            </div>
            <input :class="{'border-2 border-red-500': form.errors['ein.ein_activity_specified']}"
                   v-model="form.ein.ein_activity_specified" type="text"
                   name="ein_activity_specified" id="ein-activity-specified" autocomplete="off"
                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                   placeholder="Enter Business Activity"/>

          </div>
          <div v-if="form.errors['ein.ein_activity_specified']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_activity_specified']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sm:col-span-2">
        <h2 class="text-md font-medium mt-2">7. If you offer a service, which type of Service do you offer? If you sell
          merchandise, what principle line of merchandise do
          you sell?</h2>
      </div>
      <div>
        <div class="mt-1">
          <input :class="{'border-2 border-red-500': form.errors['ein.ein_merchandise']}"
                 v-model="form.ein.ein_merchandise" type="text"
                 name="ein_merchandise" id="ein-merchandise" autocomplete="off"
                 class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder="Service or Merchandise Type"/>
          <div v-if="form.errors['ein.ein_merchandise']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_merchandise']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>


      <div class="sm:col-span-2 mt-6">
        <h2 class="text-md font-medium mt-2">8. Has the responsible party ever applied for a company EIN
          before?</h2>
      </div>
      <div class="sm:col-span-2">


        <div class="space-y-5">

          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_before" value="0" id="ein-before-no" aria-describedby="medium-description"
                     name="ein_before" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="ein-before-no" class="font-medium text-gray-900">No</label>
            </div>
          </div>

          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input v-model="form.ein.ein_before" value="1" id="ein-before-yes" aria-describedby="medium-description"
                     name="ein_before" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
            <div class="ml-3 text-sm leading-6 w-2/4">
              <label for="ein-before-yes" class="font-medium text-gray-900">Yes</label>
            </div>
            <input :class="{'border-2 border-red-500': form.errors['ein.ein_before_specified']}"
                   v-model="form.ein.ein_before_specified" type="text"
                   name="ein_before_specified" id="ein-before-specified" autocomplete="off"
                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                   placeholder="If Yes, enter EIN"/>

          </div>
          <div v-if="form.errors['ein.ein_before_specified']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.ein_before_specified']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>


      <div class="sm:col-span-2 mt-6">
        <h2 class="text-md font-medium mt-2">9. Briefly, within 2-3 sentences, please tell us what your business
          does. </h2>
      </div>

      <div class="sm:col-span-2">
        <div class="space-y-5">
                  <textarea :class="{'border-2 border-red-500': form.errors['ein.ein_further_info']}"
                            rows="4"
                            v-model="form.ein.ein_further_info"
                            name="further-info" id="ein-further-info"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Describe what your business does."></textarea>
        </div>
      </div>

      <div class="sm:col-span-2 mt-6">
        <h2 class="text-md font-medium mt-2">10. Answer these yes or no questions about your business.</h2>
      </div>

      <div class="lg:col-span-2 border-2 bg-white rounded p-4 space-y-8">
        <div class="lg:col-span-2" :class="{'border-2 border-red-500 p-2': form.errors['ein.question.heavy_vehicle']}">
          <div class="w-full">
            Does your business own a highway motor vehicle with a taxable gross weight of 55,000 pounds or more?
          </div>
          <div class="flex items-center space-x-8 mt-2">
            <div class="flex items-center">
              <label for="ein-question-heavy-vehicle-yes" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.heavy_vehicle']}">Yes</label>
              <input v-model="form.ein.question.heavy_vehicle" value="1" id="ein-question-heavy-vehicle-yes" name="heavy_vehicle" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>

            <div class="flex items-center">
              <label for="ein-question-heavy-vehicle-no" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.heavy_vehicle']}">No</label>
              <input v-model="form.ein.question.heavy_vehicle" value="0" id="ein-question-heavy-vehicle-no" name="heavy_vehicle" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
          </div>
          <div v-if="form.errors['ein.question.heavy_vehicle']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.question.heavy_vehicle']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>

        <div class="lg:col-span-2" :class="{'border-2 border-red-500 p-2': form.errors['ein.question.gambling']}">
          <div class="w-full">
            Does your business involve gambling/wagering?
          </div>
          <div class="flex items-center space-x-8 mt-2">
            <div class="flex items-center">
              <label for="ein-question-gambling-yes" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.gambling']}">Yes</label>
              <input v-model="form.ein.question.gambling" value="1" id="ein-question-gambling-yes" name="gambling" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>

            <div class="flex items-center">
              <label for="ein-question-gambling-no" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.gambling']}">No</label>
              <input v-model="form.ein.question.gambling" value="0" id="ein-question-gambling-no" name="gambling" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
          </div>
          <div v-if="form.errors['ein.question.gambling']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.question.gambling']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>

        <div class="lg:col-span-2" :class="{'border-2 border-red-500 p-2': form.errors['ein.question.quarterly']}">
          <div class="w-full">
            Does your business need to file Form 720 (Quarterly Federal Excise Tax Return)?
          </div>
          <div class="flex items-center space-x-8 mt-2">
            <div class="flex items-center">
              <label for="ein-question-quarterly-yes" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.quarterly']}">Yes</label>
              <input v-model="form.ein.question.quarterly" value="1" id="ein-question-quarterly-yes" name="quarterly" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>

            <div class="flex items-center">
              <label for="ein-question-quarterly-no" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.quarterly']}">No</label>
              <input v-model="form.ein.question.quarterly" value="0" id="ein-question-quarterly-no" name="quarterly" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
          </div>
          <div v-if="form.errors['ein.question.quarterly']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.question.quarterly']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
        <div class="lg:col-span-2" :class="{'border-2 border-red-500 p-2': form.errors['ein.question.guns']}">
          <div class="w-full">
            Does your business sell or manufacture alcohol, tobacco, or firearms?
          </div>
          <div class="flex items-center space-x-8 mt-2">
            <div class="flex items-center">
              <label for="ein-question-guns-yes" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.guns']}">Yes</label>
              <input v-model="form.ein.question.guns" value="1" id="ein-question-guns-yes" name="guns" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>

            <div class="flex items-center">
              <label for="ein-question-guns-no" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.guns']}">No</label>
              <input v-model="form.ein.question.guns" value="0" id="ein-question-guns-no" name="guns" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
          </div>
          <div v-if="form.errors['ein.question.guns']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.question.guns']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
        <div class="lg:col-span-2" :class="{'border-2 border-red-500 p-2': form.errors['ein.question.w2']}">
          <div class="w-full">
            Do you have, or do you expect to have, any employees who will receive Forms W-2 in the next 12 months?*
            (Forms W-2 require additional filings with the IRS.)
          </div>
          <div class="flex items-center space-x-8 mt-2">
            <div class="flex items-center">
              <label for="ein-question-w2-yes" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.w2']}">Yes</label>
              <input v-model="form.ein.question.w2" value="1" id="ein-question-w2-yes" name="w2" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>

            <div class="flex items-center">
              <label for="ein-question-w2-no" class="font-medium text-gray-900 mr-2"
                     :class="{'text-red-600': form.errors['ein.question.w2']}">No</label>
              <input v-model="form.ein.question.w2" value="0" id="ein-question-w2-no" name="w2" type="radio"
                     class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
            </div>
          </div>

          <div v-if="form.errors['ein.question.w2']" class="m-2">
            <ul>
              <li class="text-red-600 text-sm" v-for="(error, index) in form.errors['ein.question.w2']"
                  :key="index">{{
                  error
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>


      <div
          :class="[
            'sm:col-span-2 mt-8',
            needsGenerateNudge
              ? 'border-2 border-red-500 bg-red-50 rounded-md p-4'
              : 'border-t border-gray-200 pt-6'
          ]">
        <h2 class="text-lg font-medium">11. Review and Confirm Your IRS Forms</h2>
        <p class="text-sm text-gray-600 mt-1">
          Generate your EIN application (Form SS-4) and Tax Information Authorization (Form 8821) below. Download each form, review it carefully for accuracy, and then confirm by checking the box below.
        </p>

        <div v-if="!itemUuid" class="mt-6">
          <p class="mb-4 text-sm leading-relaxed text-slate-600">
            Your signature below will be reproduced verbatim on
            <span class="font-medium text-slate-900">Form SS-4</span>
            and
            <span class="font-medium text-slate-900">Form 8821</span>
            as the responsible party.
          </p>

          <div class="relative">
            <!-- Anchored label tab -->
            <div class="absolute -top-3 left-5 z-10 inline-flex items-center gap-2 rounded-md bg-slate-900 px-3.5 py-1.5 text-white shadow-md ring-1 ring-slate-900/10">
              <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                <path d="M21.71 5.29l-3-3a1 1 0 0 0-1.42 0L3 16.59V21h4.41L21.71 6.7a1 1 0 0 0 0-1.41ZM6.59 19H5v-1.59l9.59-9.58 1.58 1.58L6.59 19ZM18.17 7.41l-1.58-1.58L18 4.41 19.59 6l-1.42 1.41Z"/>
              </svg>
              <span class="font-serif text-[11px] font-bold uppercase tracking-[0.4em]">Signature</span>
            </div>

            <!-- Signing surface — warm bond-paper tone, hairline frame, layered ornament -->
            <div class="relative overflow-hidden rounded-lg border border-slate-900 bg-[#fdfcf6] pt-7 shadow-[0_1px_0_0_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.18)]">
              <!-- Subtle ledger-grid texture -->
              <div
                  class="pointer-events-none absolute inset-0 opacity-[0.05]"
                  style="background-image: linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px); background-size: 28px 28px;">
              </div>

              <!-- Decorative corner brackets — legal-document framing -->
              <span class="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 border-l-2 border-t-2 border-slate-900"></span>
              <span class="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 border-r-2 border-t-2 border-slate-900"></span>
              <span class="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 border-l-2 border-b-2 border-slate-900"></span>
              <span class="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 border-r-2 border-b-2 border-slate-900"></span>

              <!-- Canvas — unchanged API; only height + pen color tuned to match the new palette -->
              <VueSignaturePad
                  ref="signaturePad"
                  width="100%"
                  height="220px"
                  :options="{ onBegin: onSignatureBegin, penColor: '#0f172a', backgroundColor: 'rgba(0,0,0,0)' }"
              />

              <!-- Signing baseline with ✗ marker — convention from real IRS forms -->
              <div class="pointer-events-none absolute bottom-9 left-8 right-8 flex items-end gap-3">
                <span class="font-serif text-2xl font-bold leading-none text-slate-700">&#10007;</span>
                <div class="mb-1 flex-1 border-b border-dashed border-slate-500"></div>
              </div>

              <!-- Caption strip below the baseline -->
              <div class="pointer-events-none absolute bottom-3.5 left-8 right-8 flex items-center justify-between">
                <span class="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">Sign Above</span>
                <span class="font-serif text-xs italic text-slate-600">Responsible Party &middot; IRS SS-4 / F-8821</span>
              </div>
            </div>
          </div>
          <div class="mt-2 flex items-center justify-between">
            <p class="text-xs text-gray-500">Use your mouse or finger to sign in the box above.</p>
            <button
                type="button"
                @click="clearSignature"
                class="text-xs text-indigo-600 hover:text-indigo-500">
              Clear signature
            </button>
          </div>
          <div
              v-if="submitGateError"
              class="mt-3 rounded-md bg-red-50 border border-red-300 p-3 text-sm text-red-700">
            {{ submitGateError }}
          </div>
        </div>

        <div class="mt-4 space-y-4">
          <div class="flex items-center gap-3">
            <span
                v-if="needsGenerateNudge"
                class="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-red-600 ein-nudge-bounce">
              Click here
              <ArrowRightIcon class="h-5 w-5" aria-hidden="true"/>
            </span>
            <button
                type="button"
                :disabled="generating"
                @click="generateReviewForms"
                :class="[
                  'rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60',
                  needsGenerateNudge
                    ? 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600 ring-2 ring-red-300'
                    : 'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600'
                ]">
              <span v-if="generating">Generating forms…</span>
              <span v-else-if="formsReady">Regenerate forms</span>
              <span v-else>Generate forms for review</span>
            </button>
          </div>

          <div v-if="generateError" class="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {{ generateError }}
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
                v-if="ss4Url"
                :href="ss4Url"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-3 rounded-md border border-gray-200 px-4 py-3 hover:bg-gray-50">
              <ArrowDownTrayIcon class="h-6 w-6 text-indigo-600 shrink-0" aria-hidden="true"/>
              <div>
                <div class="text-sm font-medium text-gray-900">Form SS-4 — EIN Application</div>
                <div class="text-xs text-gray-500">Click to download and review</div>
              </div>
            </a>
            <div v-else class="flex items-center gap-3 rounded-md border border-dashed border-gray-200 px-4 py-3 text-gray-400">
              <ArrowDownTrayIcon class="h-6 w-6 shrink-0" aria-hidden="true"/>
              <div class="text-sm">Form SS-4 will appear here once generated</div>
            </div>

            <a
                v-if="f8821Url"
                :href="f8821Url"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-3 rounded-md border border-gray-200 px-4 py-3 hover:bg-gray-50">
              <ArrowDownTrayIcon class="h-6 w-6 text-indigo-600 shrink-0" aria-hidden="true"/>
              <div>
                <div class="text-sm font-medium text-gray-900">Form 8821 — Tax Information Authorization</div>
                <div class="text-xs text-gray-500">Click to download and review</div>
              </div>
            </a>
            <div v-else class="flex items-center gap-3 rounded-md border border-dashed border-gray-200 px-4 py-3 text-gray-400">
              <ArrowDownTrayIcon class="h-6 w-6 shrink-0" aria-hidden="true"/>
              <div class="text-sm">Form 8821 will appear here once generated</div>
            </div>
          </div>

        </div>
      </div>


    </div>

  </div>

</template>

<style scoped>
@keyframes einNudgeBounce {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(8px); }
}
.ein-nudge-bounce {
  animation: einNudgeBounce 0.9s ease-in-out infinite;
}
</style>

